import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Subject, noop } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  DocumentListItemResponse,
  PspBranchResponse,
  baseRelatedPspBranchesSelectors,
  BaseCoreState,
  BaseToastAlertClasses,
  baseRelatedPspBranchActions,
  BaseModalService,
  baseSharedActions,
  BaseDocumentsService,
  DocumentListItemResponseType,
} from 'base';

@Component({
  selector: 'base-customer-provider-details-master-data',
  templateUrl: './base-customer-provider-details-master-data.component.html',
})
export class BaseCustomerProviderDetailsMasterDataComponent implements OnInit, OnDestroy {
  @Input() pspId: number | undefined;
  @ViewChild('documentViewerTemplate') documentViewerTemplate: ElementRef;
  providerDetails: PspBranchResponse | undefined;
  documents: DocumentListItemResponseType[] = [];
  activeDocumentSrc: Blob;
  activeDocument: DocumentListItemResponse;
  documentViewerModalRef: NgbModalRef;
  documentCategories: { value: number; label: string }[] = [];

  private destroy$ = new Subject<boolean>();

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private baseDocumentsService: BaseDocumentsService
  ) {}

  ngOnInit(): void {
    this.store
      .select(baseRelatedPspBranchesSelectors.getRelatedPspBranchDetails)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((state) => state.data as PspBranchResponse)
      )
      .subscribe((details) => {
        if (details) {
          this.providerDetails = details;
        }
      });

    combineLatest([
      this.baseDocumentsService.getDocumentCategories$('psp'),
      this.store.select(baseRelatedPspBranchesSelectors.getRelatedPspBranchDocuments),
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(([categoryState, documentsState]) => !!categoryState && !!documentsState.ok),
        map(([categoriesRes, documentsRes]) => ({
          categoriesRes: categoriesRes as { value: number; label: string }[],
          documentsRes: documentsRes.data as DocumentListItemResponse[],
        }))
      )
      .subscribe(({ categoriesRes, documentsRes }) => {
        if (categoriesRes && documentsRes) {
          this.documentCategories = categoriesRes;

          this.documents = (documentsRes as DocumentListItemResponse[]).map((document) => ({
            document,
            categoryLabel: this.documentCategories.find((category) => category.value === document.category)?.label,
          }));
        }
      });

    this.store
      .select(baseRelatedPspBranchesSelectors.getRelatedPspBranchDownloadDocument)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.data),
        map((state) => state.data)
      )
      .subscribe((document) => {
        this.openDocumentViewer(document as Blob);
      });
  }

  ngOnDestroy(): void {
    this.store.dispatch(baseRelatedPspBranchActions.clearDownloadRelatedPspBranchDocument());
    this.destroy$.next(true);
  }

  copyToClipboard(text: string): void {
    if (text) {
      navigator.clipboard.writeText(text).then(() => {
        this.store.dispatch(
          baseSharedActions.addSystemAlert({
            payload: {
              class: BaseToastAlertClasses.success,
              body: 'successMessage.copiedToClipboard',
            },
          })
        );
      });
    }
  }

  downloadDocument(event: { id: string; action: string }, document: DocumentListItemResponseType) {
    if (!this.pspId) {
      return;
    }

    this.activeDocument = document.document;
    const openInNewWindow = event.action === 'open';
    this.store.dispatch(
      baseRelatedPspBranchActions.downloadRelatedPspBranchDocument({
        payload: {
          document: event.id || this.activeDocument.id,
          documentType: this.activeDocument.type,
          psp: this.pspId,
          openInNewWindow,
        },
      })
    );
  }

  private openDocumentViewer(document: Blob): void {
    this.activeDocumentSrc = document;
    this.documentViewerModalRef = this.modalService.open(
      {
        bodyTemplate: this.documentViewerTemplate,
        cancelButtonDisplayed: false,
        okButtonDisplayed: false,
        hideHeader: true,
        hideFooter: true,
      },
      { size: 'xl' }
    );
    this.documentViewerModalRef.result.then(noop, noop);
  }
}
